import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { SeedbedShell } from "../../components/programs/SeedbedShell"
import { SeedbedInfoForm, EMPTY_SEEDBED_FORM, type SeedbedFormFields } from "../../components/programs/SeedbedInfoForm"
import { slugify } from "@/lib/slugify"

type Institution = { id: string; name: string }

function toIsoDate(date: string): string | undefined {
  if (!date) return undefined
  return new Date(`${date}T00:00:00.000Z`).toISOString()
}

export default function SeedbedFormPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [form, setForm] = useState<SeedbedFormFields>({
    ...EMPTY_SEEDBED_FORM,
    institution_id: searchParams.get("institution_id") ?? "",
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin/institutions?limit=200&page=1")
      .then((res) => setInstitutions(res.data?.data?.institutions ?? []))
      .catch(() => setInstitutions([]))
  }, [])

  function goBack() {
    navigate("/programs/seedbeds")
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      const res = await api.post("/academy/seedbeds/admin", {
        institution_id: form.institution_id,
        name: form.name,
        slug: slugify(form.name),
        description: form.description || undefined,
        status: form.status,
        start_date: toIsoDate(form.start_date),
        end_date: toIsoDate(form.end_date),
      })
      qc.invalidateQueries({ queryKey: ["server-table", "/academy/seedbeds/admin"] })
      const newId = res.data?.data?.id
      navigate(newId ? `/programs/seedbeds/${newId}` : "/programs/seedbeds")
    } catch (err) {
      setError(getErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <SeedbedShell
      mode="create"
      activeTab="info"
      onTabChange={() => {}}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Semilleros", to: "/programs/seedbeds" },
        "Nuevo",
      ]}
      title="Nuevo semillero"
      subtitle="Programa o grupo de estudio con cursos y estudiantes asignados. Grupos y cursos se habilitan al guardar."
    >
      <form onSubmit={onSubmit}>
        <SeedbedInfoForm form={form} setForm={setForm} institutions={institutions} isCreate />

        {error ? (
          <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={goBack} className={btnSecondary}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} className={btnSave}>
            {saving ? "Creando..." : "Crear semillero"}
          </button>
        </div>
      </form>
    </SeedbedShell>
  )
}
